<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="600px"
    title="Add to block list"
  >
    <div
      :id="`${source}_add_phone_block_list`"
      class="flex flex-col h-full justify-between"
    >
      <PhoneBlockListForm
        :source="source"
        :formData="formData"
        @update:formData="updateFormData"
        @validateForm="validateForm"
      />
    </div>

    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button
          class="primary_medium"
          @click="toAddPhoneToBlockList"
          :disabled="!canAdd"
          :loading="addingToBlockList"
        >
          {{ addingToBlockList ? "Adding..." : "Add to block list" }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, watch, reactive, computed } from "vue";
import { message } from "ant-design-vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import PhoneBlockListForm from "@/src/components/block_list_components/PhoneBlockListForm.vue";
import { cleanPhoneNumber } from "@/src/utils/phoneNumberUtils.js";
import DashboardService from "@/src/api/DashboardService.js";
import { addToDNDBlocklist } from "@/src/utils/callsUtils.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  drawerVisible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:modelValue", "complete"]);

const addingToBlockList = ref(false);
const canAdd = ref(false);
const internalDrawerVisible = ref(props.drawerVisible);

const formData = reactive({
  phoneNumber: "",
  name: "",
  dndStatus: "do_not_call",
  existingContactData: null,
});

const updateFormData = (newData) => {
  Object.assign(formData, newData);
};

const validateForm = (isValid) => {
  canAdd.value = isValid;
};

const toAddPhoneToBlockList = async () => {
  try {
    addingToBlockList.value = true;

    const cleanedNumber = cleanPhoneNumber(formData.phoneNumber);

    let entityId = null;
    let entityType = null;

    if (formData.existingContactData) {
      entityId = formData.existingContactData.id;
      entityType = formData.existingContactData.type;
    } 
   
    else if (formData.name.trim()) {
      try {
        const nameParts = formData.name.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const contactPayload = {
          email: "",
          first_name: firstName,
          last_name: lastName,
          phone_number: cleanedNumber,
          contact_status: "New",
        };

        const contactResponse = await DashboardService.addContact(contactPayload);

        if (contactResponse?.success) {
          entityId = contactResponse.contact?.id;
          entityType = entityTypes.CONTACT;
        } 
      } catch (error) {
        console.error("Failed to create contact:", error);
        message.error("Failed to create contact");
        return;
      }
    }

    if (!cleanedNumber) {
      message.error("Please enter a valid phone number");
      return;
    }

    if (!entityId || !entityType) {
      message.error("Name is required to add to block list");
      return;
    }

    const entity = { id: entityId, type: entityType };
    const result = await addToDNDBlocklist(cleanedNumber, entity, formData.dndStatus);

    if (result.success) {
      message.success("Phone number added to block list successfully");
      internalDrawerVisible.value = false;
      emit("complete", true);
    } else {
      message.error("Failed to add phone number to block list");
      return;
    }

  } catch (error) {
    console.error("Error adding to block list:", error);
    message.error("Failed to add phone number to block list");
  } finally {
    addingToBlockList.value = false;
  }
};


watch(
  () => props.drawerVisible,
  (newVal) => {
    internalDrawerVisible.value = newVal;
  },
);

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    if (!newVal) {
      formData.phoneNumber = "";
      formData.name = "";
      formData.dndStatus = "do_not_call";
      formData.existingContactData = null;
      canAdd.value = false;
      emit("update:modelValue", false);
    }
  },
);
</script>
